import { useMediaQuery } from '@mui/material';
import { alpha, darken, lighten, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import type { IStyleDTO } from '@sharedInterfaces/ICompanySettings';
import saveStyle from '@src/APIs/graphQl/Company/saveStyle';
import { setWindowSize } from '@store/reducer/windowSizeReducer';
import type { AppState } from '@store/store';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { defaultColors } from '../../sharedInterfaces/defaultColors';

import { generateTheme } from './themeGenerator';

export enum EDarkMode {
    DARK = 'dark',
    LIGHT = 'light',
    DEFAULT = 'automatic',
}

type ThemeContextType = {
    isDarkMode: boolean;
    defaultColors: typeof defaultColors;
    primaryColor: string;
    onPrimaryColor: string;
    secondaryColor: string;
    onSecondaryColor: string;
    userDarkModeSettings: EDarkMode;
    changeDarkMode: (mode: EDarkMode) => void;
    resetTheme: () => void;
    isDefaultTheme: (tempPrimaryColor?: string, tempSecondaryColor?: string) => boolean;
    saveStyle: (newStyle: IStyleDTO) => Promise<IStyleDTO>;

    setPrimaryColor: (color: string) => void;
    setOnPrimaryColor: (color: string) => void;
    setSecondaryColor: (color: string) => void;
    setOnSecondaryColor: (color: string) => void;
};
interface ThemeProviderProps {
    children: React.ReactNode;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: ThemeProviderProps) {
    const dispatch = useDispatch();
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const dialogCount = useSelector((state: AppState) => state.dialogs.dialogs.length);
    const style = useSelector((state: AppState) => state.company.style);

    const [userDarkModeSettings, setUserDarkModeSettings] = useState<EDarkMode>(() => {
        const savedMode = localStorage.getItem('userDarkModeSettings');
        return savedMode ? (savedMode as EDarkMode) : EDarkMode.DEFAULT;
    });
    const [isDarkMode, setIsDarkMode] = useState(
        userDarkModeSettings === EDarkMode.DEFAULT ? prefersDarkMode : userDarkModeSettings === EDarkMode.DARK,
    );
    const [primaryColor, setPrimaryColor] = useState(defaultColors.light.primary);
    const [onPrimaryColor, setOnPrimaryColor] = useState(defaultColors.light.onPrimary);
    const [secondaryColor, setSecondaryColor] = useState(defaultColors.light.secondary);
    const [onSecondaryColor, setOnSecondaryColor] = useState(defaultColors.light.onSecondary);
    // Load theme from redux store
    useEffect(() => {
        setPrimaryColor(style.primary);
        setOnPrimaryColor(style.onPrimary);
        setSecondaryColor(style.secondary);
        setOnSecondaryColor(style.onSecondary);
    }, [style]);

    useEffect(() => {
        if (userDarkModeSettings === EDarkMode.DEFAULT && isDarkMode !== prefersDarkMode) {
            setIsDarkMode(prefersDarkMode);
        }
    }, [prefersDarkMode, userDarkModeSettings]);
    // Synchronize with CSS variables
    useEffect(() => {
        if (typeof window === 'undefined') return;
        // Dark Mode CSS class
        if (isDarkMode) {
            document.documentElement.classList.add('dark-theme');
        } else {
            document.documentElement.classList.remove('dark-theme');
        }

        // CSS Custom Properties
        document.documentElement.style.setProperty('--color-primary', primaryColor);
        document.documentElement.style.setProperty('--color-on-primary', onPrimaryColor);
        document.documentElement.style.setProperty('--color-secondary', secondaryColor);
        document.documentElement.style.setProperty('--color-on-secondary', onSecondaryColor);

        document.documentElement.style.setProperty('--color-primary-light', lighten(primaryColor, 0.2));
        document.documentElement.style.setProperty('--color-primary-dark', darken(primaryColor, 0.2));
        document.documentElement.style.setProperty('--color-secondary-light', lighten(secondaryColor, 0.2));
        document.documentElement.style.setProperty('--color-secondary-dark', darken(secondaryColor, 0.2));

        document.documentElement.style.setProperty('--h1-font-family', 'Bricolage Grotesque, Inter, Arial, sans-serif');
        document.documentElement.style.setProperty('--h1-font-size', '2.25rem');
        document.documentElement.style.setProperty('--h1-font-weight', '600');
        document.documentElement.style.setProperty('--h1-line-height', '1.2');
        document.documentElement.style.setProperty('--h1-letter-spacing', '-0.05em');
        document.documentElement.style.setProperty(
            '--text-primary-color',
            isDarkMode ? defaultColors.dark.text : defaultColors.light.text,
        );

        document.documentElement.style.setProperty('--h3-font-family', 'Bricolage Grotesque, Inter, Arial, sans-serif');
        document.documentElement.style.setProperty('--h3-font-size', '1.5rem');
        document.documentElement.style.setProperty('--h3-font-weight', '400');
        document.documentElement.style.setProperty('--h3-line-height', '1.3');
        document.documentElement.style.setProperty('--h3-letter-spacing', 'normal');

        document.documentElement.style.setProperty('--h4-font-family', 'Bricolage Grotesque, Inter, Arial, sans-serif');
        document.documentElement.style.setProperty('--h4-font-size', '1.4rem');
        document.documentElement.style.setProperty('--h4-font-weight', '400');
        document.documentElement.style.setProperty('--h4-line-height', '1.3');
        document.documentElement.style.setProperty('--h4-letter-spacing', 'normal');

        // Gradient für den Hintergrund
        const backgroundGradient = isDarkMode
            ? `linear-gradient(135deg, #252526 0%, #1e1e1e 100%)`
            : `linear-gradient(to bottom right, ${alpha(primaryColor, 0.08)}, ${alpha(secondaryColor, 0.1)})`;
        document.documentElement.style.setProperty('--background-gradient', backgroundGradient);

        // Set primary color RGB for CSS variable
        const primaryColorRgb = hexToRgb(primaryColor);
        document.documentElement.style.setProperty(
            '--color-primary-rgb',
            primaryColorRgb ? `${primaryColorRgb.r}, ${primaryColorRgb.g}, ${primaryColorRgb.b}` : '64, 61, 219',
        );
    }, [isDarkMode, primaryColor, onPrimaryColor, secondaryColor, onSecondaryColor]);

    // Changes size for Dialog bar
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--var-dialogBar-height', dialogCount ? '45px' : '0px');
    }, [dialogCount]);

    //Update sizes in redux on rezize
    useEffect(() => {
        function handleResize() {
            dispatch(setWindowSize(window.innerWidth, window.innerHeight));
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [dispatch]);

    // Wenn Primary sich geändert hat dann theme-color ändern...
    useEffect(() => {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', style.primary);
    }, [style.primary]);

    // Theme functions
    const changeDarkMode = (mode: EDarkMode) => {
        let newDarkMode: boolean;

        setUserDarkModeSettings(mode);
        localStorage.setItem('userDarkModeSettings', mode);

        if (mode === EDarkMode.DEFAULT) {
            newDarkMode = prefersDarkMode;
        } else {
            newDarkMode = mode === EDarkMode.DARK;
        }
        setIsDarkMode(newDarkMode);
        setPrimaryColor(newDarkMode ? defaultColors.dark.primary : defaultColors.light.primary);
        setOnPrimaryColor(newDarkMode ? defaultColors.dark.onPrimary : defaultColors.light.onPrimary);
        setSecondaryColor(newDarkMode ? defaultColors.dark.secondary : defaultColors.light.secondary);
        setOnSecondaryColor(newDarkMode ? defaultColors.dark.onSecondary : defaultColors.light.onSecondary);
    };

    const resetTheme = () => {
        // setIsDarkMode(false);
        setPrimaryColor(defaultColors.light.primary);
        setOnPrimaryColor(defaultColors.light.onPrimary);
        setSecondaryColor(defaultColors.light.secondary);
        setOnSecondaryColor(defaultColors.light.onSecondary);
    };

    const isDefaultTheme = (tempPrimaryColor?: string, tempSecondaryColor?: string) =>
        (tempPrimaryColor ? tempPrimaryColor : primaryColor) === defaultColors.light.primary &&
        (tempSecondaryColor ? tempSecondaryColor : secondaryColor) === defaultColors.light.secondary;

    const theme = generateTheme({
        isDarkMode,
        primaryColor,
        secondaryColor,
        defaultColors,
    });
    return (
        <MuiThemeProvider theme={theme}>
            <ThemeContext.Provider
                value={{
                    isDarkMode,
                    defaultColors,
                    primaryColor,
                    onPrimaryColor,
                    secondaryColor,
                    onSecondaryColor,
                    userDarkModeSettings,
                    changeDarkMode,
                    resetTheme,
                    isDefaultTheme,
                    saveStyle: saveStyle,
                    setPrimaryColor,
                    setOnPrimaryColor,
                    setSecondaryColor,
                    setOnSecondaryColor,
                }}
            >
                {children}
            </ThemeContext.Provider>
        </MuiThemeProvider>
    );
}

export function useAppTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useAppTheme must be used within a ThemeProvider');
    }
    return context;
}

// Helper function to convert hex to RGB
function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}
